import { isRetryableApiError } from '@/shared/api/client';
import {
  LOCAL_UPLOAD_MAX_AUTO_RETRIES,
  LOCAL_UPLOAD_RETRY_BASE_MS,
  LOCAL_UPLOAD_RETRY_MAX_MS,
} from '../config/constants';
import type { LocalUploadRetryOperation } from '../types/uploads';

export type { LocalUploadRetryOperation } from '../types/uploads';

type LocalUploadTransport = 'vision_lab_api' | 'gcs_resumable_fetch' | 'gcs_resumable_xhr';

interface RetryableErrorLike {
  status?: unknown;
  xhrStatus?: unknown;
  retryAfterMs?: unknown;
}

export interface LocalUploadRetryEvent {
  operation: LocalUploadRetryOperation;
  transport: LocalUploadTransport;
  /** One-based automatic retry number; the initial request is attempt zero. */
  attempt: number;
  maxRetries: number;
  delayMs: number;
  waitingForNetwork: boolean;
  error: unknown;
}

interface RetryWaitInput {
  operation: LocalUploadRetryOperation;
  transport: LocalUploadTransport;
  attempt: number;
  error: unknown;
  signal?: AbortSignal;
  onRetry?: (event: LocalUploadRetryEvent) => void;
  random?: () => number;
}

interface RetryOperationInput<T> {
  operation: LocalUploadRetryOperation;
  transport: LocalUploadTransport;
  run: () => Promise<T>;
  signal?: AbortSignal;
  onRetry?: (event: LocalUploadRetryEvent) => void;
}

function errorLike(error: unknown): RetryableErrorLike {
  return error && typeof error === 'object' ? (error as RetryableErrorLike) : {};
}

function abortError(): DOMException {
  return new DOMException('aborted', 'AbortError');
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) return Promise.reject(abortError());
  return new Promise((resolve, reject) => {
    const finish = () => {
      signal?.removeEventListener('abort', cancel);
      resolve();
    };
    const cancel = () => {
      clearTimeout(timer);
      reject(abortError());
    };
    const timer = setTimeout(finish, ms);
    signal?.addEventListener('abort', cancel, { once: true });
  });
}

function waitForOnline(signal?: AbortSignal): Promise<void> {
  if (typeof navigator === 'undefined' || navigator.onLine) return Promise.resolve();
  if (signal?.aborted) return Promise.reject(abortError());
  return new Promise((resolve, reject) => {
    const finish = () => {
      globalThis.removeEventListener('online', finish);
      signal?.removeEventListener('abort', cancel);
      resolve();
    };
    const cancel = () => {
      globalThis.removeEventListener('online', finish);
      reject(abortError());
    };
    globalThis.addEventListener('online', finish, { once: true });
    signal?.addEventListener('abort', cancel, { once: true });
  });
}

/** Upload retries are deliberately narrower than generic HTTP retries. Authentication, permission,
 * validation and not-found responses require user/business action and must never be retried. */
export function isRetryableUploadError(error: unknown): boolean {
  if (error instanceof TypeError || isRetryableApiError(error)) return true;
  const { status, xhrStatus } = errorLike(error);
  const code = typeof status === 'number' ? status : xhrStatus;
  return typeof code === 'number' && (code === 0 || code === 408 || code === 429 || code >= 500);
}

/** Full-jitter exponential backoff. A server Retry-After value is authoritative when it is longer. */
function localUploadRetryDelayMs(
  attempt: number,
  error: unknown,
  random: () => number = Math.random,
): number {
  const ceiling = Math.min(
    LOCAL_UPLOAD_RETRY_BASE_MS * 2 ** Math.max(0, attempt - 1),
    LOCAL_UPLOAD_RETRY_MAX_MS,
  );
  const jittered = Math.floor(Math.max(0, Math.min(1, random())) * ceiling);
  const retryAfter = errorLike(error).retryAfterMs;
  return typeof retryAfter === 'number' && Number.isFinite(retryAfter)
    ? Math.max(jittered, Math.max(0, retryAfter))
    : jittered;
}

/** Wait for one scheduled retry. Offline time does not spin or consume additional attempts: the
 * promise first waits for the browser's `online` event, then applies the bounded jitter delay. */
export async function waitForLocalUploadRetry({
  operation,
  transport,
  attempt,
  error,
  signal,
  onRetry,
  random,
}: RetryWaitInput): Promise<void> {
  const waitingForNetwork = typeof navigator !== 'undefined' && !navigator.onLine;
  const delayMs = localUploadRetryDelayMs(attempt, error, random);
  onRetry?.({
    operation,
    transport,
    attempt,
    maxRetries: LOCAL_UPLOAD_MAX_AUTO_RETRIES,
    delayMs,
    waitingForNetwork,
    error,
  });
  if (waitingForNetwork) await waitForOnline(signal);
  await sleep(delayMs, signal);
}

/** Retry a read or idempotent transition. Ambiguous chunk/progress/complete writes require their own
 * reconciliation before replay and intentionally do not use this helper directly. */
export async function runRetryableUploadOperation<T>({
  operation,
  transport,
  run,
  signal,
  onRetry,
}: RetryOperationInput<T>): Promise<T> {
  for (let retries = 0; ; retries += 1) {
    try {
      return await run();
    } catch (error) {
      if (!isRetryableUploadError(error) || retries >= LOCAL_UPLOAD_MAX_AUTO_RETRIES) throw error;
      await waitForLocalUploadRetry({
        operation,
        transport,
        attempt: retries + 1,
        error,
        signal,
        onRetry,
      });
    }
  }
}
